// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * cm-coding: Knowledge-base instances are workspace guidance libraries whose
 * items are injected into analysis prompts. Administrators arrive here using
 * product terms such as "instance" and "mode," so the page explains how those
 * settings affect analysis instead of requiring backend documentation before
 * they can safely configure a library. Item schemas remain beside instances
 * because they define the entries each library accepts.
 */
import { useCallback, useState, type FC } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

import { SegmentedTabs } from '@/components/ui'
import { ConfigFlowStrip } from '@/app-shell/components/ConfigFlowStrip'
import { KnowledgeBaseUserViewWrapper } from '@/subsystems/knowledge-base/shared/components/UserViewWrapper'
import { InstancesTab } from '@/subsystems/knowledge-base/admin/InstancesTab'
import { SchemasTab } from '@/subsystems/knowledge-base/admin/SchemasTab'

const TABS = {
  instances: 'instances',
  schemas: 'schemas',
} as const

type KbAdminTab = (typeof TABS)[keyof typeof TABS]

interface ViewTarget {
  orgId: string
  wsId: string
  instanceId: string
  label: string
}

const KnowledgeBasesPage: FC = () => {
  const [viewing, setViewing] = useState<ViewTarget | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const currentTab = (searchParams.get('tab') ?? TABS.instances) as KbAdminTab

  const handleTabChange = useCallback(
    (tabId: KbAdminTab) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          next.set('tab', tabId)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  if (viewing) {
    return (
      <div className="min-h-full bg-slate-50 text-slate-900">
        <div className="mx-auto max-w-[1280px] space-y-4 px-6 pt-9 pb-16 sm:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-[21px] font-[650] tracking-[-0.015em]">{viewing.label}</h1>
              <p className="mt-[5px] font-mono text-[11.5px] text-slate-500">
                {viewing.orgId} / {viewing.wsId} / {viewing.instanceId}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setViewing(null)}
              className="inline-flex items-center gap-1.5 rounded-[9px] border border-slate-200 bg-white px-3.5 py-[7px] text-[12.5px] font-[550] text-slate-700 shadow-[0_1px_2px_rgba(2,8,23,0.04)] transition-colors duration-[120ms] hover:bg-slate-50"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to list
            </button>
          </div>
          <KnowledgeBaseUserViewWrapper
            orgId={viewing.orgId}
            workspaceId={viewing.wsId}
            nodeDefinition="knowledge_base"
            nodeInstanceId={viewing.instanceId}
            label={viewing.label}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-[1280px] px-6 pt-9 pb-16 sm:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[21px] font-[650] tracking-[-0.015em]">Knowledge Bases</h1>
            <p className="mt-[5px] text-[13px] text-slate-500">
              Guidance libraries the AI receives when it analyzes documents — one instance per workspace need
            </p>
          </div>
          <SegmentedTabs
            tabs={[
              { id: TABS.instances, label: 'Instances' },
              { id: TABS.schemas, label: 'Item Schemas' },
            ]}
            value={currentTab}
            onChange={handleTabChange}
          />
        </div>
        <div className="mt-6">
          <ConfigFlowStrip current="knowledge-bases" />
        </div>
        <div className="mt-6">
          {currentTab === TABS.schemas ? <SchemasTab /> : <InstancesTab onView={setViewing} />}
        </div>
      </div>
    </div>
  )
}

export default KnowledgeBasesPage
